<script setup lang="ts">
import type { FineUploaderUpload } from '@/models/current-student-advanced-standing-form/api'
import {
  formatBytes,
  uploadStatusClass,
  uploadStatusText,
} from '@/models/current-student-advanced-standing-form/upload'

defineProps<{
  attachments: FineUploaderUpload[]
  uploadThumbnailUrl: (attachment: FineUploaderUpload) => string
}>()

const emit = defineEmits<{
  cancelStagedUpload: [attachment: FineUploaderUpload]
  retryUpload: [attachment: FineUploaderUpload]
  deleteStagedUpload: [attachment: FineUploaderUpload]
}>()
</script>

<template>
  <ul class="qq-upload-list-selector qq-upload-list" role="region" aria-live="polite" aria-relevant="additions removals">
    <li
      v-for="attachment in attachments"
      :key="attachment.uuid ?? attachment.name"
      :class="uploadStatusClass(attachment)"
    >
      <span role="status" class="qq-upload-status-text-selector qq-upload-status-text">{{ uploadStatusText(attachment) }}</span>
      <div class="qq-progress-bar-container-selector qq-progress-bar-container" :class="{ 'qq-hide': attachment.status !== 'uploading' && attachment.status !== 'retrying upload' }">
        <div role="progressbar" aria-valuenow="100" aria-valuemin="0" aria-valuemax="100" class="qq-progress-bar-selector qq-progress-bar" :style="{ width: attachment.status === 'uploading' || attachment.status === 'retrying upload' ? '100%' : '0%' }"></div>
      </div>
      <span class="qq-upload-spinner-selector qq-upload-spinner" :class="{ 'qq-hide': attachment.status !== 'uploading' && attachment.status !== 'retrying upload' }"></span>
      <div class="qq-thumbnail-wrapper">
        <img
          class="qq-thumbnail-selector"
          :class="{ 'qq-hide': uploadThumbnailUrl(attachment) === '' }"
          :src="uploadThumbnailUrl(attachment) || undefined"
          qq-max-size="120"
          qq-server-scale
          :alt="uploadThumbnailUrl(attachment) ? attachment.name : undefined"
        >
      </div>
      <button
        v-if="attachment.status === 'uploading' || attachment.status === 'retrying upload' || attachment.status === 'upload failed'"
        type="button"
        class="qq-upload-cancel-selector qq-upload-cancel"
        @click="emit('cancelStagedUpload', attachment)"
      >
        X
      </button>
      <button
        type="button"
        class="qq-upload-retry-selector qq-upload-retry"
        :class="{ 'qq-hide': attachment.status !== 'upload failed' }"
        @click="emit('retryUpload', attachment)"
      >
        <span class="qq-btn qq-retry-icon" aria-label="Retry"></span>
        Retry
      </button>
      <div class="qq-file-info">
        <div class="qq-file-name">
          <span class="qq-upload-file-selector qq-upload-file">{{ attachment.name }}</span>
          <span class="qq-edit-filename-icon-selector qq-edit-filename-icon" aria-label="Edit filename"></span>
        </div>
        <input class="qq-edit-filename-selector qq-edit-filename" tabindex="0" type="text">
        <span class="qq-upload-size-selector qq-upload-size">{{ formatBytes(attachment.size ?? 0) }}</span>
        <button
          v-if="attachment.status === 'upload successful'"
          type="button"
          class="qq-btn qq-upload-delete-selector qq-upload-delete"
          @click="emit('deleteStagedUpload', attachment)"
        >
          <span class="qq-btn qq-delete-icon" aria-label="Delete"></span>
        </button>
        <button type="button" class="qq-btn qq-upload-pause-selector qq-upload-pause qq-hide">
          <span class="qq-btn qq-pause-icon" aria-label="Pause"></span>
        </button>
        <button type="button" class="qq-btn qq-upload-continue-selector qq-upload-continue qq-hide">
          <span class="qq-btn qq-continue-icon" aria-label="Continue"></span>
        </button>
      </div>
    </li>
  </ul>
</template>
